import { CloseOutlined, ExclamationCircleTwoTone } from '@ant-design/icons';
import type { Field } from '@evidia/components';
import { ConfirmModal, SimpleForm } from '@evidia/components';
import type { ArrayElement } from '@evidia/shared';
import { toCzDateStringWithHours } from '@evidia/shared';
import { Button, Typography } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { RULE_PAYMENT_ITEM_PRICE } from '../../../const/forms';
import { useActiveSchoolId } from '../../../hooks/useActiveSchoolId';
import { useSocketMessage } from '../../../hooks/useSocketMessage';
import { formatToCZK } from '../../../utils/currency';
import type { RouterOutput } from '../../../utils/trcp';
import { trpc } from '../../../utils/trcp';
import SimpleDrawer from '../../SimpleDrawer';
import SimpleTable from '../../SimpleTable/SimpleTable';

type PaymentItemRecord = ArrayElement<RouterOutput['paymentItem']['getPaymentItems']>;

const DrawerStyled = styled(SimpleDrawer)`
	.ant-drawer-body {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		overflow-x: hidden;
	}
`;

const ImageContainer = styled.div`
	flex: 1;
	width: calc(100% + 48px);
	translate: -24px 0;
	background-image: url('/evidia_platba.png');
	background-size: contain;
	background-repeat: no-repeat;
	background-position: center;
`;

const FIELDS: Field[] = [
	{
		key: 'price',
		label: 'Platba',
		placeholder: '1000',
		type: 'number',
		htmlInputType: 'number',
		required: true,
		rules: [RULE_PAYMENT_ITEM_PRICE],
	},
];

interface Props {
	studentId: string;
}

const PaymentTab: React.FC<Props> = ({ studentId }) => {
	const { activeSchoolId } = useActiveSchoolId();
	const [isOpen, setIsOpen] = useState(false);
	const createPayment = trpc.paymentItem.createPayment.useMutation();
	const getPaymentItems = trpc.paymentItem.getPaymentItems.useQuery({
		schoolId: activeSchoolId,
		studentId,
	});

	const removePayment = trpc.paymentItem.removePayment.useMutation();

	useSocketMessage('PaymentItemMessage', d => {
		if (d.studentId === studentId) {
			getPaymentItems.refetch();
		}
	});

	const onFinishLocal = async (values: { price: number }) => {
		await createPayment.mutateAsync({ price: values.price, schoolId: activeSchoolId, studentId });
		setIsOpen(false);
	};

	const onDeletePayment = async (id: string) => {
		await removePayment.mutateAsync({ id, schoolId: activeSchoolId, studentId });
		await getPaymentItems.refetch();
	};

	const columns = [
		{
			title: 'Datum',
			render: (item: PaymentItemRecord) => <span>{toCzDateStringWithHours(item.createdAt)}</span>,
		},
		{
			title: 'Částka',
			render: (item: PaymentItemRecord) => <span>{formatToCZK(item.price)}</span>,
		},
		{
			title: 'Akce',
			render: (item: PaymentItemRecord) => (
				<ConfirmModal
					title={
						<>
							<ExclamationCircleTwoTone twoToneColor="#ff0000" />
							{' Odstranit platbu'}
						</>
					}
					description="Opravdu chcete odstranit platbu?"
					onConfirm={() => onDeletePayment(item.id)}
					okText="Ano"
					cancelText="Zrušit"
				>
					<Button type="text" icon={<CloseOutlined />} />
				</ConfirmModal>
			),
		},
	];

	return (
		<>
			<SimpleTable
				border
				count={{ data: getPaymentItems.data?.length ?? -1, loading: getPaymentItems.isLoading }}
				data={{
					data: (getPaymentItems?.data ?? []) as any[],
					loading: getPaymentItems.isLoading,
				}}
				columns={columns}
			/>
			<Button type="primary" onClick={() => setIsOpen(true)}>
				Zaevidovat platbu
			</Button>
			<DrawerStyled open={isOpen} onClose={() => setIsOpen(false)}>
				<Typography.Title level={3}>Evidence platby</Typography.Title>
				<ImageContainer />
				<SimpleForm
					loading={createPayment.isLoading}
					fields={FIELDS}
					onFinish={onFinishLocal}
					submitButtonTitle="Zaevidovat platbu"
				/>
			</DrawerStyled>
		</>
	);
};

export default PaymentTab;
